import React, { useState } from 'react';

/**
 * Composant Liste des Notes - Vue compacte de toutes les notes
 * S'ouvre en modal pleine page
 */
const NotesListView = ({ notes, onClose, onUpdateNote, onSave }) => {
  const [editingId, setEditingId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Gérer la fermeture avec auto-sauvegarde
  const handleClose = () => {
    if (hasUnsavedChanges) {
      // Auto-sauvegarde avant fermeture
      if (onSave) {
        onSave();
      }
      alert('✅ Auto-sauvegarde effectuée avant fermeture !');
    }
    if (onClose) {
      onClose();
    }
  };

  // Styles
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 10000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  };

  const containerStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  };

  const headerStyle = {
    padding: '20px 25px',
    borderBottom: '2px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
  };

  const contentStyle = {
    padding: '25px',
    overflowY: 'auto',
    flex: 1,
  };

  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s',
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#667eea',
    color: 'white',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#e0e0e0',
    color: '#333',
  };

  const successButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#10b981',
    color: 'white',
  };

  const listItemStyle = {
    padding: '15px 20px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  };

  const dateStyle = {
    fontSize: '11px',
    color: '#9ca3af',
    fontStyle: 'italic',
    marginLeft: '10px',
  };

  const titleStyle = {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px',
  };

  const previewStyle = {
    fontSize: '13px',
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '500px',
  };

  const editTextareaStyle = {
    width: '100%',
    minHeight: '150px',
    padding: '12px',
    borderRadius: '6px',
    border: '2px solid #667eea',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginTop: '10px',
  };

  // Gérer le début de l'édition
  const handleStartEdit = (note) => {
    const noteId = note.id || note._id;
    setEditingId(noteId);
    setEditedContent(note.content);
  };

  // Gérer la sauvegarde de l'édition
  const handleSaveEdit = async (note) => {
    const noteId = note.id || note._id;
    if (onUpdateNote) {
      await onUpdateNote(noteId, { content: editedContent });
      setHasUnsavedChanges(true); // Marquer comme ayant des changements
    }
    setEditingId(null);
    setEditedContent('');
  };

  // Gérer l'annulation de l'édition
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedContent('');
  };

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={containerStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', color: '#1f2937' }}>
              📋 Liste des Notes
            </h2>
            <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              {notes.length} note{notes.length > 1 ? 's' : ''} au total
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              style={successButtonStyle} 
              onClick={() => {
                if (onSave) {
                  onSave();
                  setHasUnsavedChanges(false);
                }
                alert('✅ Toutes les modifications ont été sauvegardées !');
              }}
              title="Sauvegarder toutes les modifications"
            >
              💾 Sauvegarde
            </button>
            <button style={secondaryButtonStyle} onClick={handleClose}>
              ✕ Fermer
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {notes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📝</div>
              <p style={{ fontSize: '16px' }}>Aucune note disponible</p>
            </div>
          ) : (
            <div>
              {notes.map((note, index) => {
                const noteId = note.id || note._id;
                const isEditing = editingId === noteId;

                return (
                  <div 
                    key={noteId}
                    style={{
                      ...listItemStyle,
                      backgroundColor: isEditing ? '#f0f4ff' : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                    }}
                    onMouseEnter={(e) => {
                      if (!isEditing) e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      if (!isEditing) e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {/* Titre et date */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={titleStyle}>
                          {index + 1}. {note.title}
                        </span>
                        <span style={dateStyle}>
                          {formatDate(note.created_at)}
                        </span>
                      </div>

                      {/* Contenu ou zone d'édition */}
                      {isEditing ? (
                        <div>
                          <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            style={editTextareaStyle}
                            placeholder="Modifiez le contenu de la note..."
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button 
                              style={primaryButtonStyle}
                              onClick={() => handleSaveEdit(note)}
                            >
                              ✓ Valider
                            </button>
                            <button 
                              style={secondaryButtonStyle}
                              onClick={handleCancelEdit}
                            >
                              ✕ Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={previewStyle}>
                            {note.content}
                          </div>
                          {note.bible_reference && (
                            <div style={{ fontSize: '12px', color: '#667eea', marginTop: '4px' }}>
                              📖 {note.bible_reference}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Bouton Modifier */}
                    {!isEditing && (
                      <button
                        style={{
                          ...buttonStyle,
                          padding: '8px 16px',
                          fontSize: '13px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          marginLeft: '15px',
                        }}
                        onClick={() => handleStartEdit(note)}
                      >
                        ✏️ Modifier
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer avec info */}
        <div style={{
          padding: '15px 25px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          fontSize: '12px',
          color: '#6b7280',
          textAlign: 'center',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
        }}>
          💡 Cliquez sur "Modifier" pour éditer une note • Les modifications sont auto-sauvegardées à la fermeture {hasUnsavedChanges && '• ⚠️ Modifications non sauvegardées'}
        </div>
      </div>
    </div>
  );
};

export default NotesListView;
